import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  getAllVerifications,
  updateVerification,
  fetchVerificationDocument
} from '../../api/services/verificationsService';
import { getAllUsersWithToken } from '../../api/services/usersService';
import { can } from '../../lib/permissions';
import { relativeTime } from '../../hooks/usePendingItems';
import { Avatar, Band, BigButton, Card, HeaderActions, ICONS, LineIcon, Segmented, SectionLabel, StatusPill } from '../../components/ui/dg';

const TYPE_LABELS: Record<string, string> = {
  identity: 'Pièce d’identité',
  license: 'Permis de conduire',
  address: 'Justificatif de domicile',
  phone: 'Téléphone'
};

// L'API renvoie les dates du permis en 'YYYY-MM-DD' : on les formate sans
// passer par Date, pour éviter le décalage d'un jour que provoque
// l'interprétation UTC d'une date sans heure.
const formatDay = (value?: string | null): string => {
  if (!value) return '—';
  const [year, month, day] = value.slice(0, 10).split('-');
  return year && month && day ? `${day}/${month}/${year}` : value;
};

const formatMoment = (value?: string | null): string => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
};

const isExpired = (value?: string | null): boolean =>
  Boolean(value) && value!.slice(0, 10) < new Date().toISOString().slice(0, 10);

interface Verification {
  id_verification: string;
  user_id: string;
  type: string;
  status: string;
  document_front_url?: string | null;
  document_back_url?: string | null;
  admin_notes?: string | null;
  created_at?: string;
  verification_date?: string | null;
  driver_license?: {
    license_number?: string;
    categories?: string[];
    issue_date?: string | null;
    expiry_date?: string | null;
    holder_first_name?: string | null;
    holder_last_name?: string | null;
  } | null;
}

interface UserSummary {
  id_user: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  profile_photo?: string | null;
}

type Tab = 'pending' | 'history';


/**
 * File de traitement des documents déposés.
 *
 * Deux onglets plutôt qu'un filtre : instruire ce qui attend et consulter ce
 * qui a été décidé sont deux gestes différents, et le second ne doit pas
 * encombrer le premier.
 *
 * Les lignes sont repliées par défaut. Une file se parcourt d'abord du regard
 * — qui, quoi, depuis quand — et les images ne se chargent qu'une fois la
 * ligne ouverte : à trente dossiers, tout afficher revenait à télécharger
 * soixante images pour n'en regarder qu'une.
 */
const Verifications: React.FC = () => {
  const { token, user } = useAuth();
  const [verifications, setVerifications] = useState<Verification[]>([]);
  const [users, setUsers] = useState<Record<string, UserSummary>>({});
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<Tab>('pending');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [statusMenuId, setStatusMenuId] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [docUrls, setDocUrls] = useState<Record<string, string>>({});
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const [searchParams] = useSearchParams();
  const focusId = searchParams.get('focus');
  const canReview = can(user?.role, 'verification.review');
  // Revenir sur une décision prise n'est pas de la modération : le serveur le
  // réserve aux admins, l'écran doit dire la même chose.
  const canOverride = can(user?.role, 'verification.override');

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    Promise.all([
      getAllVerifications(token).catch(() => []),
      getAllUsersWithToken(token).catch(() => [])
    ])
      .then(([rawVerifications, rawUsers]) => {
        setVerifications(Array.isArray(rawVerifications) ? rawVerifications : []);
        const byId: Record<string, UserSummary> = {};
        (Array.isArray(rawUsers) ? rawUsers : []).forEach((entry: UserSummary) => {
          byId[entry.id_user] = entry;
        });
        setUsers(byId);
      })
      .finally(() => setLoading(false));
  }, [token]);

  // Le menu de statut se ferme dès qu'on clique ailleurs : un menu resté
  // ouvert sur une ligne pendant qu'on en lit une autre finit par être cliqué
  // par erreur.
  useEffect(() => {
    if (!statusMenuId) return;
    const close = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('[data-status-menu]')) {
        setStatusMenuId(null);
        setRejectingId(null);
        setRejectReason('');
      }
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [statusMenuId]);

  const pending = useMemo(
    () => verifications.filter((v) => v.status === 'pending'),
    [verifications]
  );
  const history = useMemo(
    () => verifications.filter((v) => v.status !== 'pending'),
    [verifications]
  );
  const visible = tab === 'pending' ? pending : history;

  /**
   * Dernier dossier déposé par chaque personne.
   *
   * Un compte peut légitimement en avoir plusieurs — un refus, puis un nouveau
   * dépôt accepté. Les anciens restent affichés dans l'historique, et rien ne
   * distinguait celui qui fait foi : approuver l'ancien redonnerait un permis
   * qu'on avait précisément écarté.
   */
  const latestByUser = useMemo(() => {
    const latest: Record<string, Verification> = {};
    verifications.forEach((entry) => {
      const known = latest[entry.user_id];
      if (!known || new Date(entry.created_at || 0) > new Date(known.created_at || 0)) {
        latest[entry.user_id] = entry;
      }
    });
    return latest;
  }, [verifications]);

  // Dossier désigné depuis la cloche : on ouvre le bon onglet, on déplie la
  // ligne et on l'amène sous les yeux — sans quoi le clic déposerait
  // l'utilisateur en haut d'une liste où il devrait chercher.
  useEffect(() => {
    if (!focusId || loading) return;
    const target = verifications.find((v) => v.id_verification === focusId);
    if (!target) return;
    setTab(target.status === 'pending' ? 'pending' : 'history');
    setExpandedId(focusId);
    window.setTimeout(() => {
      document.getElementById(`verification-${focusId}`)?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest'
      });
    }, 100);
  }, [focusId, loading, verifications]);

  // Les images ne sont servies que derrière le jeton, ce qu'une balise <img
  // src> ne sait pas envoyer : on les télécharge en blob, et seulement pour la
  // ligne ouverte.
  useEffect(() => {
    if (!token || !expandedId) return;
    const target = verifications.find((v) => v.id_verification === expandedId);
    if (!target) return;

    let cancelled = false;
    const created: string[] = [];

    (async () => {
      for (const side of ['front', 'back'] as const) {
        const key = `${expandedId}:${side}`;
        const stored = side === 'front' ? target.document_front_url : target.document_back_url;
        if (!stored || docUrls[key]) continue;
        try {
          const url = await fetchVerificationDocument(expandedId, side, token);
          if (cancelled) {
            URL.revokeObjectURL(url);
            return;
          }
          created.push(url);
          setDocUrls((prev) => ({ ...prev, [key]: url }));
        } catch {
          // Document illisible ou effacé du disque : la vignette reste absente,
          // la ligne se traite quand même.
        }
      }
    })();

    return () => {
      cancelled = true;
      created.forEach((url) => URL.revokeObjectURL(url));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedId, token, verifications]);

  const review = async (id: string, status: 'approved' | 'rejected', adminNotes?: string) => {
    if (!token) return;
    setBusyId(id);
    try {
      const updated = await updateVerification(id, { status, admin_notes: adminNotes }, token);
      setVerifications((prev) =>
        prev.map((v) => (v.id_verification === id ? { ...v, ...updated } : v))
      );
      setToast({
        message: status === 'approved' ? 'Vérification approuvée.' : 'Vérification refusée.',
        type: 'success'
      });
      setExpandedId(null);
    } catch (err: any) {
      setToast({ message: err.message || 'Échec de la mise à jour.', type: 'error' });
    } finally {
      setBusyId(null);
      setRejectingId(null);
      setRejectReason('');
    }
  };

  const userLabel = (userId: string): string => {
    const entry = users[userId];
    if (!entry) return 'Utilisateur inconnu';
    return `${entry.first_name || ''} ${entry.last_name || ''}`.trim() || entry.email || 'Utilisateur';
  };

  const holderMismatch = (verification: Verification): boolean => {
    const license = verification.driver_license;
    if (!license) return false;
    const normalize = (value?: string | null) =>
      (value || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
    const declared = `${normalize(license.holder_first_name)} ${normalize(license.holder_last_name)}`.trim();
    if (!declared) return false;
    const entry = users[verification.user_id];
    const account = `${normalize(entry?.first_name)} ${normalize(entry?.last_name)}`.trim();
    return declared !== account;
  };

  // La file se traite dans l'ordre d'arrivée ; l'historique, du plus récent.
  const ordered = useMemo(() => {
    const byDate = (a: Verification, b: Verification) => new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime();
    return tab === 'pending' ? [...visible].sort(byDate) : [...visible].sort((a, b) => byDate(b, a));
  }, [visible, tab]);

  // Un dossier est toujours ouvert à droite : le premier de la file à défaut.
  useEffect(() => {
    if (loading) return;
    if (!expandedId || !ordered.some((v) => v.id_verification === expandedId)) {
      setExpandedId(ordered[0]?.id_verification || null);
    }
  }, [ordered, expandedId, loading]);

  const current = ordered.find((v) => v.id_verification === expandedId) || null;
  const photoOf = (url?: string | null) => (!url ? null : /^https?:\/\//.test(url) ? url : `${process.env.REACT_APP_API_URL || ''}${url.replace(/^\//, '')}`);

  const Skel = ({ className }: { className: string }) => <div className={`${className} animate-pulse rounded-lg bg-[#EEF0F5]`} />;

  const detail = (verification: Verification) => {
    const license = verification.driver_license;
    const mismatch = holderMismatch(verification);
    const expired = isExpired(license?.expiry_date);
    const superseded = latestByUser[verification.user_id]?.id_verification !== verification.id_verification;
    const entry = users[verification.user_id];
    const busy = busyId === verification.id_verification;
    const rejecting = rejectingId === verification.id_verification;
    const fields: { label: string; value: string; ok: boolean | null }[] = [
      { label: 'Titulaire déclaré', value: [license?.holder_first_name, license?.holder_last_name].filter(Boolean).join(' ') || '—', ok: license ? !mismatch : null },
      { label: 'Numéro', value: license?.license_number || '—', ok: license?.license_number ? true : null },
      { label: 'Catégories', value: (license?.categories || []).join(', ') || '—', ok: (license?.categories || []).length ? true : null },
      { label: 'Délivré le', value: formatDay(license?.issue_date), ok: null },
      { label: 'Valable jusqu’au', value: formatDay(license?.expiry_date), ok: license?.expiry_date ? !expired : null },
    ];
    const statusPill = verification.status === 'pending'
      ? <StatusPill tone="amber">À vérifier</StatusPill>
      : verification.status === 'approved' ? <StatusPill tone="green">Approuvé</StatusPill> : <StatusPill tone="red">Refusé</StatusPill>;
    return (
      <Card className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <div className="flex items-center gap-3.5 px-[22px] py-[18px]">
          <Avatar name={userLabel(verification.user_id)} photo={photoOf(entry?.profile_photo)} size={48} />
          <div className="min-w-0 flex-1">
            <b className="block truncate text-[17px] font-extrabold text-[#1E293B]">{userLabel(verification.user_id)}</b>
            <span className="block truncate text-[12.5px] text-[#6B7280]">
              {TYPE_LABELS[verification.type] || verification.type} · déposé le {formatMoment(verification.created_at)}{entry?.email ? ` · ${entry.email}` : ''}
            </span>
          </div>
          {statusPill}
        </div>
        <Band />
        <div className="min-h-0 flex-1 overflow-y-auto">
          {(mismatch || superseded || expired) && (
            <div className="mx-[22px] mt-4 space-y-1 rounded-xl bg-[#FDECEF] px-3.5 py-2.5 text-[12.5px] font-semibold text-[#B91C1C]">
              {mismatch && <p>Le nom du permis diffère de celui du compte.</p>}
              {expired && <p>Le permis est expiré.</p>}
              {superseded && <p className="text-[#B45309]">Cette personne a déposé un document plus récent, celui-ci ne fait plus foi.</p>}
            </div>
          )}
          <div className="grid grid-cols-1 gap-4 px-[22px] pt-[18px] sm:grid-cols-2">
            {(['front', 'back'] as const).map((side) => {
              const stored = side === 'front' ? verification.document_front_url : verification.document_back_url;
              const url = docUrls[`${verification.id_verification}:${side}`];
              return (
                <div key={side}>
                  <SectionLabel>{side === 'front' ? 'Recto' : 'Verso'}</SectionLabel>
                  {stored ? (
                    <a href={url} target="_blank" rel="noreferrer"
                      className={`mt-2 block h-[170px] overflow-hidden rounded-[14px] border border-[#E5E7EB] ${url ? 'bg-[#F8FAFC]' : 'animate-pulse bg-[#EEF0F5]'}`}>
                      {url && <img src={url} alt={side === 'front' ? 'Recto' : 'Verso'} className="h-full w-full object-contain" />}
                    </a>
                  ) : (
                    <div className="mt-2 flex h-[170px] items-center justify-center rounded-[14px] border border-dashed border-[#E5E7EB] text-[12.5px] text-[#9CA3AF]">Non déposé</div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="grid grid-cols-1 gap-4 px-[22px] pb-[18px] pt-[18px] sm:grid-cols-2">
            <div>
              <SectionLabel>Photo du profil</SectionLabel>
              <div className="mt-2 flex items-center gap-3">
                <Avatar name={userLabel(verification.user_id)} photo={photoOf(entry?.profile_photo)} size={84} tone="grey" />
                <p className="text-[13px] leading-[1.5] text-[#4B5563]">Comparer le visage avec la photo du permis.</p>
              </div>
            </div>
            <div>
              <SectionLabel>Lu sur le document</SectionLabel>
              {fields.map((f, i) => (
                <div key={f.label} className="flex items-center gap-1.5 py-2.5 text-[13px]" style={{ borderTop: i ? '1px solid #EEF0F4' : 'none' }}>
                  <span className="flex-1 text-[#6B7280]">{f.label}</span>
                  <b className={`font-bold ${f.ok === false ? 'text-[#B91C1C]' : 'text-[#1E293B]'}`}>{f.value}</b>
                  {f.ok === true && <LineIcon d={ICONS.check} size={15} color="#047857" width={2.6} />}
                  {f.ok === false && <LineIcon d={ICONS.alert} size={15} color="#B91C1C" width={2.2} />}
                </div>
              ))}
            </div>
          </div>
          {verification.status !== 'pending' && (verification.verification_date || verification.admin_notes) && (
            <div className="mx-[22px] mb-4 rounded-xl bg-[#F7F7FB] px-3.5 py-2.5 text-[12.5px] text-[#4B5563]">
              {verification.verification_date && <p>Décidé le {formatDay(verification.verification_date)}</p>}
              {verification.admin_notes && <p>Motif enregistré : {verification.admin_notes}</p>}
            </div>
          )}
        </div>

        {/* Décision */}
        {((verification.status === 'pending' && canReview) || (verification.status !== 'pending' && canOverride && !superseded)) && (
          <div className="border-t border-[#EEF0F4] px-[22px] py-4">
            {rejecting ? (
              <div className="flex flex-wrap items-center gap-3">
                <input autoFocus value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Motif du refus, envoyé à la personne dans sa langue"
                  className="h-12 min-w-[240px] flex-1 rounded-2xl border border-[#F5C2CB] px-4 text-[14px] focus:outline-none focus:ring-4 focus:ring-[#B91C1C]/10" />
                <BigButton kind="secondary" onClick={() => { setRejectingId(null); setRejectReason(''); }}>Annuler</BigButton>
                <BigButton kind="danger" disabled={busy || !rejectReason.trim()} onClick={() => review(verification.id_verification, 'rejected', rejectReason.trim())}>Confirmer le refus</BigButton>
              </div>
            ) : (
              <div className="flex flex-wrap items-center gap-3">
                <span className="min-w-[200px] flex-1 text-[12.5px] text-[#6B7280]">
                  {verification.status === 'pending' ? 'Refuser demande un motif, envoyé à la personne dans sa langue.' : 'Revenir sur la décision prise.'}
                </span>
                {verification.status !== 'rejected' && (
                  <BigButton kind="danger" className="w-[150px]" disabled={busy} onClick={() => { setRejectingId(verification.id_verification); setRejectReason(''); }}>Refuser</BigButton>
                )}
                {verification.status !== 'approved' && (
                  <BigButton className="w-[190px]" disabled={busy} onClick={() => review(verification.id_verification, 'approved')}>
                    <LineIcon d={ICONS.check} size={18} color="#fff" width={2.6} /> Approuver
                  </BigButton>
                )}
              </div>
            )}
          </div>
        )}
      </Card>
    );
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden bg-[#F7F7FB] px-4 pb-6 pt-1 lg:px-7">
      <HeaderActions>
        <Segmented
          ariaLabel="Dossiers"
          value={tab}
          onChange={(id) => setTab(id as Tab)}
          options={[
            { id: 'pending', label: `En attente${pending.length ? ` · ${pending.length}` : ''}` },
            { id: 'history', label: 'Historique' }
          ]}
        />
      </HeaderActions>
      <div className="lg:hidden">
        <Segmented ariaLabel="Dossiers" value={tab} onChange={(id) => setTab(id as Tab)}
          options={[{ id: 'pending', label: `En attente${pending.length ? ` · ${pending.length}` : ''}` }, { id: 'history', label: 'Historique' }]} />
      </div>

      {toast && (
        <div className={`rounded-2xl px-4 py-3 text-[13px] font-semibold ${toast.type === 'success' ? 'bg-[#ECFDF3] text-[#047857]' : 'bg-[#FDECEF] text-[#B91C1C]'}`}>
          {toast.message}
        </div>
      )}

      <div className="flex min-h-0 flex-1 flex-col gap-4 lg:flex-row">
        {/* La file */}
        <Card className="flex max-h-[40vh] w-full shrink-0 flex-col overflow-hidden px-2 py-2.5 lg:max-h-none lg:w-[330px]">
          <div className="flex items-center justify-between px-3 pb-2 pt-2">
            <SectionLabel>{tab === 'pending' ? `${pending.length} en attente` : `${history.length} décidés`}</SectionLabel>
            <span className="text-[12px] text-[#9CA3AF]">{tab === 'pending' ? 'plus anciens d’abord' : 'plus récents d’abord'}</span>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3 px-3.5 py-3"><Skel className="h-[38px] w-[38px] rounded-full" /><div className="flex-1 space-y-2"><Skel className="h-3.5 w-28" /><Skel className="h-3 w-20" /></div></div>
              ))
            ) : ordered.length === 0 ? (
              <p className="px-4 py-10 text-center text-[13px] text-[#6B7280]">{tab === 'pending' ? 'Rien à traiter pour le moment.' : 'Aucune décision enregistrée.'}</p>
            ) : (
              ordered.map((v) => {
                const selected = v.id_verification === expandedId;
                const license = v.driver_license;
                const sub = [
                  license?.categories?.length ? `Permis ${license.categories.join(', ')}` : TYPE_LABELS[v.type] || v.type,
                  holderMismatch(v) ? 'nom différent' : isExpired(license?.expiry_date) ? 'expiré' : null
                ].filter(Boolean).join(' · ');
                return (
                  <button key={v.id_verification} id={`verification-${v.id_verification}`} type="button"
                    onClick={() => { setExpandedId(v.id_verification); setRejectingId(null); }}
                    className={`flex w-full items-center gap-3 rounded-[14px] px-3.5 py-3 text-left transition-colors ${
                      selected ? 'bg-[#F7F6FF] shadow-[inset_0_0_0_1.5px_#C7C4F6]' : 'hover:bg-[#FBFBFE]'
                    }`}>
                    <Avatar name={userLabel(v.user_id)} photo={photoOf(users[v.user_id]?.profile_photo)} size={38} tone={selected ? 'indigo' : 'grey'} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[14px] font-semibold text-[#1E293B]">{userLabel(v.user_id)}</span>
                      <span className={`block truncate text-[12px] ${holderMismatch(v) || isExpired(license?.expiry_date) ? 'text-[#B91C1C]' : 'text-[#6B7280]'}`}>{sub}</span>
                    </span>
                    {tab === 'pending' ? (
                      <span className="shrink-0 text-[12px] text-[#9CA3AF]">{relativeTime(v.created_at)}</span>
                    ) : v.status === 'approved' ? <StatusPill tone="green">Approuvé</StatusPill> : <StatusPill tone="red">Refusé</StatusPill>}
                  </button>
                );
              })
            )}
          </div>
        </Card>

        {/* Le dossier */}
        {current ? detail(current) : !loading && (
          <Card className="flex flex-1 items-center justify-center px-6 py-16 text-center">
            <div>
              <p className="text-[17px] font-extrabold text-[#1E293B]">{tab === 'pending' ? 'Tout est traité' : 'Aucun dossier'}</p>
              <p className="mt-1.5 text-[13.5px] text-[#6B7280]">{tab === 'pending' ? 'Les nouveaux permis apparaîtront ici.' : 'Les décisions prises apparaîtront ici.'}</p>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

export default Verifications;
